<div class="sidenav-content">
  <div class="bcontainer2">
    <div class="main-content">
    <div class="spacer5"></div>

    <div class="pcontainter">
      <div class="hero-section">
        <h1>Welcome to XDBrain</h1>
        <p class="tagline">
          The intelligent memory framework that makes your apps think like humans
        </p>
      </div>

      <div class="intro-section">
        <p class="intro-text">
          Tired of building apps that forget everything the moment users close them? 
          <strong>XDBrain</strong> changes that.
        </p>
        <p class="intro-text">
          We're talking next-level memory infrastructure - the kind that lets your AI agents, 
          assistants, and productivity apps actually <em>remember</em> conversations, 
          <em>understand</em> context, and <em>recall</em> details like a human brain would.
        </p>
      </div>

      <div class="spacer5"></div>

      <h2 class="section-title">What Makes XDBrain Special?</h2>

      <div class="card-container">
        <div class="card">
          <div class="card-icon-wrapper">
            <img src="assets/images/icon-memory-search.svg" class="card-icon"/>
          </div>
          <div class="card-content">
            <h3>Memory & Semantic Search</h3>
            <p class="card-description">
              Store facts, conversations, and contextual data across sessions. 
              Find anything instantly with AI-powered semantic search - no more endless scrolling.
            </p>
            <div class="card-tags">
              <span class="tag">Context Aware</span>
              <span class="tag">Instant Recall</span>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-icon-wrapper">
            <img src="assets/images/icon-calendar.svg" class="card-icon"/>
          </div>
          <div class="card-content">
            <h3>Smart Reminders & Preferences</h3>
            <p class="card-description">
              Set intelligent, time-based reminders and build truly personalized experiences 
              by tracking user preferences, habits, and patterns.
            </p>
            <div class="card-tags">
              <span class="tag">Time-Based</span>
              <span class="tag">Personalized</span>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-icon-wrapper">
            <img src="assets/images/icon-mindmap.svg" class="card-icon"/>
          </div>
          <div class="card-content">
            <h3>Mind Mapping</h3>
            <p class="card-description">
              Visualize connections between ideas, thoughts, and topics in dynamic, 
              interactive maps. Perfect for brainstorming and knowledge organization.
            </p>
            <div class="card-tags">
              <span class="tag">Visual Thinking</span>
              <span class="tag">Connected Ideas</span>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-icon-wrapper">
            <img src="assets/images/icon-knowledge.svg" class="card-icon"/>
          </div>
          <div class="card-content">
            <h3>Structured Knowledge Graphs</h3>
            <p class="card-description">
              Model complex relationships between entities using powerful ER graphs. 
              Ideal for AI reasoning, knowledge bases, and domain modeling.
            </p>
            <div class="card-tags">
              <span class="tag">Graph-Based</span>
              <span class="tag">Relationship Mapping</span>
            </div>
          </div>
        </div>
      </div>

      <div class="spacer5"></div>

      <!-- MCP Configuration Section -->
      <div class="mcp-section">
        <h2 class="section-title">🧠 Integrate with Claude via MCP</h2>
        
        <div class="mcp-intro">
          <p class="mcp-intro-text">
            Connect XDBrain Memory to Claude Desktop and give your AI assistant persistent, 
            human-like memory. Once configured, Claude can remember your preferences, recall 
            past conversations, and provide truly personalized assistance.
          </p>
        </div>

        <div class="mcp-card">
          <div class="mcp-header">
            <div class="mcp-icon-wrapper">
              <i class="fa-solid fa-plug"></i>
            </div>
            <div class="mcp-header-content">
              <h3>Claude MCP Connector</h3>
              <span class="mcp-version">Version 1.0.0</span>
            </div>
          </div>

          <div class="mcp-content">
            <div class="mcp-info-box">
              <div class="mcp-info-item">
                <strong>Connector Name:</strong>
                <span>LIAM</span>
              </div>
              <div class="mcp-info-item">
                <strong>MCP URL:</strong>
                <a href="https://claude.ai/login" target="_blank">
                  https://mcp.askliam.ai/mcp
                  <i class="fa-solid fa-external-link-alt"></i>
                </a>
              </div>
            </div>

            <div class="mcp-features">
              <h4>What You Can Do:</h4>
              <div class="mcp-features-grid">
                <div class="mcp-feature">
                  <i class="fa-solid fa-save"></i>
                  <span><strong>CreateMemory</strong> - Save preferences, facts, and context</span>
                </div>
                <div class="mcp-feature">
                  <i class="fa-solid fa-list"></i>
                  <span><strong>ListMemory</strong> - Retrieve stored memories instantly</span>
                </div>
                <div class="mcp-feature">
                  <i class="fa-solid fa-trash"></i>
                  <span><strong>ForgetMemory</strong> - Remove outdated information</span>
                </div>
                <div class="mcp-feature">
                  <i class="fa-solid fa-tags"></i>
                  <span><strong>Tag System</strong> - Organize memories by category</span>
                </div>
              </div>
            </div>

            <div class="mcp-quick-start">
              <h4>Quick Setup Steps:</h4>
              <ol class="mcp-steps">
                <li>Open Claude Desktop and click the Configure icon</li>
                <li>Select "Add custom connector"</li>
                <li>Enter connector details (name and URL above)</li>
                <li>Sign in with your Gmail account</li>
                <li>Start chatting - Claude now has memory!</li>
              </ol>
            </div>
          </div>
        </div>

        <div class="mcp-use-case-preview">
          <h4>Example Use Cases:</h4>
          <div class="mcp-use-cases">
            <div class="mcp-use-case-item">
              <span class="mcp-use-case-emoji">🎯</span>
              <div>
                <strong>Personal Assistant:</strong>
                <p>"Remember I prefer morning meetings and vegetarian restaurants"</p>
              </div>
            </div>
            <div class="mcp-use-case-item">
              <span class="mcp-use-case-emoji">💼</span>
              <div>
                <strong>Project Context:</strong>
                <p>"Keep track of my ongoing projects and their deadlines"</p>
              </div>
            </div>
            <div class="mcp-use-case-item">
              <span class="mcp-use-case-emoji">🎨</span>
              <div>
                <strong>Creative Work:</strong>
                <p>"Save my design preferences and brand guidelines"</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="spacer5"></div>

      <div class="use-cases-section">
        <h2 class="section-title">Who's This For?</h2>
        <div class="use-cases-grid">
          <div class="use-case">
            <i class="fa-solid fa-robot"></i>
            <h4>AI Agent Builders</h4>
            <p>Give your agents persistent memory and context awareness</p>
          </div>
          <div class="use-case">
            <i class="fa-solid fa-lightbulb"></i>
            <h4>Product Teams</h4>
            <p>Build apps that learn and adapt to user behavior</p>
          </div>
          <div class="use-case">
            <i class="fa-solid fa-code"></i>
            <h4>Developers</h4>
            <p>Skip building memory infrastructure from scratch</p>
          </div>
          <div class="use-case">
            <i class="fa-solid fa-brain"></i>
            <h4>AI Researchers</h4>
            <p>Experiment with human-like memory architectures</p>
          </div>
        </div>
      </div>

      <div class="spacer5"></div>

      <div class="cta-section">
        <h2 class="cta-title">Ready to Build Something Amazing?</h2>
        <p class="cta-subtitle">Get started in minutes with our quickstart guide</p>
        <a [routerLink]="['/quickstart']" class="cta-button">
          <i class="fa fa-rocket"></i>
          Start Building Now
        </a>
      </div>

      <div class="spacer3"></div>
    </div>
  </div>
  </div>
</div>